import posthog from 'posthog-js';

// Session Tracking Utilities
export const trackSessionStart = (data: {
  adminAccountId?: string;
  creatorId?: string;
  userId?: string;
  timestamp?: number;
}) => {
  posthog.capture('session_started', {
    admin_account_id: data.adminAccountId,
    creator_id: data.creatorId,
    user_id: data.userId,
    timestamp: data.timestamp || Date.now(),
  });
};

export const trackSessionActivity = (data: {
  adminAccountId?: string;
  creatorId?: string;
  userId?: string;
  timestamp?: number;
  timeSpentSeconds: number;
}) => {
  posthog.capture('session_activity', {
    admin_account_id: data.adminAccountId,
    creator_id: data.creatorId,
    user_id: data.userId,
    time_spent_seconds: data.timeSpentSeconds,
    timestamp: data.timestamp || Date.now(),
  });
};

export const trackSessionEnd = (data: {
  adminAccountId?: string;
  creatorId?: string;
  userId?: string;
  timestamp?: number;
  totalSessionTimeSeconds: number;
}) => {
  posthog.capture('session_ended', {
    admin_account_id: data.adminAccountId,
    creator_id: data.creatorId,
    user_id: data.userId,
    total_session_time_seconds: data.totalSessionTimeSeconds,
    timestamp: data.timestamp || Date.now(),
  });
};

// Content Tracking Utilities
export const trackContentView = (data: {
  contentId: string;
  contentType: string;
  creatorId: string;
  userId?: string;
  metadata?: Record<string, any>;
}) => {
  posthog.capture('content_page_viewed', {
    content_id: data.contentId,
    content_type: data.contentType,
    creator_id: data.creatorId,
    user_id: data.userId,
    timestamp: Date.now(),
    ...data.metadata,
  });
};

export const trackContentTimeSpent = (data: {
  contentId: string;
  contentType: string;
  creatorId: string;
  userId?: string;
  metadata?: Record<string, any>;
  timeSpentSeconds: number;
}) => {
  posthog.capture('content_time_spent', {
    content_id: data.contentId,
    content_type: data.contentType,
    creator_id: data.creatorId,
    user_id: data.userId,
    time_spent_seconds: data.timeSpentSeconds,
    timestamp: Date.now(),
    ...data.metadata,
  });
};

export const trackContentPageExit = (data: {
  contentId: string;
  contentType: string;
  creatorId: string;
  userId?: string;
  metadata?: Record<string, any>;
  totalTimeSpentSeconds: number;
}) => {
  posthog.capture('content_page_exit', {
    content_id: data.contentId,
    content_type: data.contentType,
    creator_id: data.creatorId,
    user_id: data.userId,
    total_time_spent_seconds: data.totalTimeSpentSeconds,
    timestamp: Date.now(),
    ...data.metadata,
  });
};

// Funnel Tracking Utilities
const getStepNumber = (stepName: string): number => {
  const stepMap: Record<string, number> = {
    landing_page: 1,
    email_entered: 2,
    account_created: 3,
    badge_claimed: 4,
    onboarding_completed: 5,
  };
  return stepMap[stepName] || 0;
};

export const trackBadgeClaimStep = (data: {
  stepName: string;
  stepNumber?: number;
  badgeId?: string;
  creatorId?: string;
  userId?: string;
  metadata?: Record<string, any>;
}) => {
  posthog.capture('badge_claim_step', {
    step_name: data.stepName,
    step_number: data.stepNumber || getStepNumber(data.stepName),
    badge_id: data.badgeId,
    creator_id: data.creatorId,
    user_id: data.userId,
    timestamp: Date.now(),
    ...data.metadata,
  });
};

// User Activity Tracking Utilities
export const trackUserActivity = (data: {
  creatorId: string;
  activityType: string;
  userId?: string;
  metadata?: Record<string, any>;
}) => {
  posthog.capture('user_activity', {
    creator_id: data.creatorId,
    activity_type: data.activityType,
    user_id: data.userId,
    timestamp: Date.now(),
    ...data.metadata,
  });
};

// Challenge Tracking Utilities
export const trackChallengeInteraction = (data: {
  challengeId: string;
  creatorId: string;
  userId?: string;
  action: string;
  metadata?: Record<string, any>;
}) => {
  posthog.capture('challenge_interaction', {
    challenge_id: data.challengeId,
    creator_id: data.creatorId,
    user_id: data.userId,
    action: data.action,
    timestamp: Date.now(),
    ...data.metadata,
  });
};

// Acquisition Tracking Utilities
export const getUTMParams = () => {
  if (typeof window === 'undefined') return {};

  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source') || undefined,
    utm_medium: urlParams.get('utm_medium') || undefined,
    utm_campaign: urlParams.get('utm_campaign') || undefined,
    utm_content: urlParams.get('utm_content') || undefined,
  };
};

export const getAcquisitionSource = (referrer?: string): string => {
  if (!referrer) return 'direct';

  if (referrer.includes('google')) return 'google';
  if (referrer.includes('facebook')) return 'facebook';
  if (referrer.includes('twitter')) return 'twitter';
  if (referrer.includes('instagram')) return 'instagram';
  if (referrer.includes('youtube')) return 'youtube';
  if (referrer.includes('tiktok')) return 'tiktok';

  return 'referral';
};

export const trackUserAcquisition = (data: {
  source: string;
  referrer?: string;
  creatorId?: string;
  utmParams?: {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
  };
}) => {
  posthog.capture('user_acquisition', {
    acquisition_source: data.source,
    referrer_url: data.referrer,
    creator_id: data.creatorId,
    timestamp: Date.now(),
    ...data.utmParams,
  });
};

// Helper function to identify user
export const identifyUser = (distinctId: string, properties?: Record<string, any>) => {
  posthog.identify(distinctId, properties);
};

// Community Tracking Utilities
export const trackPostClicked = (data: {
  postId: string;
  creatorId: string;
  userId?: string;
  contentType?: string;
  metadata?: Record<string, any>;
}) => {
  posthog.capture('post_clicked', {
    post_id: data.postId,
    creator_id: data.creatorId,
    user_id: data.userId,
    content_type: data.contentType,
    timestamp: Date.now(),
    ...data.metadata,
  });
};

export const trackPostVoted = (data: {
  postId: string;
  creatorId: string;
  userId?: string;
  action: 'upvote' | 'downvote';
  voteAction: string;
  hierarchyType: 'post' | 'comment' | 'reply';
}) => {
  posthog.capture('post_voted', {
    post_id: data.postId,
    creator_id: data.creatorId,
    user_id: data.userId,
    action: data.action,
    vote_action: data.voteAction,
    hierarchy_type: data.hierarchyType,
    timestamp: Date.now(),
  });
};

export const trackCommentReplyClicked = (data: {
  postId: string;
  creatorId: string;
  userId?: string;
  hierarchyType: 'post' | 'comment' | 'reply';
}) => {
  posthog.capture('comment_reply_clicked', {
    post_id: data.postId,
    creator_id: data.creatorId,
    user_id: data.userId,
    hierarchy_type: data.hierarchyType,
    timestamp: Date.now(),
  });
};

export const trackCommentThreadExpanded = (data: {
  commentId: string;
  postId?: string;
  creatorId: string;
  userId?: string;
  expanded: boolean;
}) => {
  posthog.capture('comment_thread_expanded', {
    comment_id: data.commentId,
    post_id: data.postId,
    creator_id: data.creatorId,
    user_id: data.userId,
    expanded: data.expanded,
    timestamp: Date.now(),
  });
};

// Message Tracking Utilities
export const trackMessageOpened = (data: {
  messageId: string;
  creatorId: string;
  isRead: boolean;
}) => {
  posthog.capture('message_opened', {
    message_id: data.messageId,
    creator_id: data.creatorId,
    is_read: data.isRead,
    timestamp: Date.now(),
  });
};

// Video Tracking Utilities
export const trackVideoPlayStarted = (data: { videoId: string; isSubscribed: boolean }) => {
  posthog.capture('video_play_started', {
    video_id: data.videoId,
    is_subscribed: data.isSubscribed,
    timestamp: Date.now(),
  });
};

export const trackVideoPlayBlocked = (data: { videoId: string }) => {
  posthog.capture('video_play_blocked', {
    video_id: data.videoId,
    timestamp: Date.now(),
  });
};
